import type { SearchParameterAvailability } from '../searchParameterAvailability';
import type {
  SearchParameters,
  SearchParameterName,
  SearchParameterValues,
} from '../searchParameters';
import {
  scopeRememberedFor,
  PreferenceScopeEnum,
  type SearchPreferences,
} from '../searchPreferences';

const parameterNameOf = (field: string): SearchParameterName =>
  field.toLowerCase() as SearchParameterName;

/**
 * A stored preference as the matching parameter's value.
 *
 * A cookie keeps a scope's licenses as a list, while the parameter is the one
 * token they spell, so a list is joined. That is safe for a parameter whose
 * row really is an array kind too: `withValues` splits a string back into
 * members for those, so neither shape can reach a reader as the wrong one.
 */
const parameterValueFor = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.length ? value.join(',') : undefined;
  return String(value);
};

/**
 * Cross-domain rule: fills the parameters a scope remembers, from the visitor's
 * stored preferences, wherever the search itself says nothing.
 *
 * A parameter the search already states always wins — a preference is a
 * memory, never an override.
 *
 * Returns `searchParameters` itself when there is nothing to fill. That is a
 * contract, not an optimisation: `with()` constructs a new instance even for
 * an empty change set, so identity is the only way a caller can tell that
 * nothing happened.
 */
const applyPreferencesForScope = (
  searchParameters: SearchParameters,
  preferences: SearchPreferences,
  scope: PreferenceScopeEnum
): SearchParameters => {
  const scoped = preferences[scope] as Record<string, unknown>;
  const changes: Partial<SearchParameterValues> = {};

  Object.entries(scoped).forEach(([field, value]) => {
    const name = parameterNameOf(field);
    if (searchParameters.states(name)) return;

    const parameterValue = parameterValueFor(value);
    if (parameterValue === undefined) return;

    Object.assign(changes, { [name]: parameterValue });
  });

  return Object.keys(changes).length
    ? searchParameters.with(changes)
    : searchParameters;
};

/**
 * Fills `groupbyevent` from the global preference when the search says nothing.
 *
 * A second pass rather than part of `applyPreferencesForScope`, because
 * `groupByEvent` is one global boolean rather than a scope-keyed field — there
 * is no `preferences[scope]` entry to index into for it — and because its
 * availability is a question only SearchParameterAvailability can answer.
 *
 * A stored `false` is filled just as a stored `true` is. That is the whole
 * point of the tri-state: grouping is put back whenever the parameter is
 * absent, so a visitor who turned grouping off has to keep saying so. Only
 * "never chose" leaves the parameter alone.
 */
const applyGroupByEventPreference = (
  searchParameters: SearchParameters,
  preferences: SearchPreferences,
  availability: SearchParameterAvailability
): SearchParameters =>
  !searchParameters.states('groupbyevent') &&
  preferences.groupByEvent !== undefined &&
  availability.for(searchParameters).has('groupbyevent')
    ? searchParameters.with({ groupbyevent: String(preferences.groupByEvent) })
    : searchParameters;

/**
 * Cross-domain rule: fills the parameters a scope remembers, from the
 * visitor's stored preferences, wherever the search itself says nothing.
 *
 * The fill without a trigger. Cold entry calls it directly — arriving is not a
 * transition, so there is no previous search to have crossed out of — and
 * `applyPreferencesOnScopeChange` is this plus the trigger.
 *
 * Returns `searchParameters` itself when there is nothing to fill, so callers
 * can use reference equality to skip work.
 */
export const applyPreferencesOnEntry = (
  searchParameters: SearchParameters,
  preferences: SearchPreferences,
  availability: SearchParameterAvailability
): SearchParameters => {
  const scope = scopeRememberedFor(searchParameters);

  // No scope at all — a video-template search, say — has nothing to remember.
  if (scope === undefined) return searchParameters;

  return applyGroupByEventPreference(
    applyPreferencesForScope(searchParameters, preferences, scope),
    preferences,
    availability
  );
};

/**
 * Cross-domain rule: fills the new scope's remembered parameters when a search
 * changes preference scope, and does nothing otherwise.
 *
 * The trigger, not just the fill. §2.3 allows exactly two — cold entry, and a
 * `family × assettype` change — and this is the second. Applying preferences
 * on every search would re-assert a filter the visitor had just cleared, which
 * is the defect the whole model exists to avoid.
 *
 * A function rather than a hook, and not `useOnSearch`'s business, because the
 * trigger is a property of a *search-parameter transition*. Every call site
 * that changes scope without going through the filter panel can call this.
 *
 * The fill itself is `applyPreferencesOnEntry`; this adds the trigger. Cold
 * entry is the other way in, and it is not a transition — there is no previous
 * search to have crossed out of — so it calls the fill directly.
 *
 * Returns `next` itself when the scope is unchanged, or when the new scope has
 * nothing stored — `applyPreferencesForScope` already guarantees that, and
 * callers can use reference equality to skip work.
 */
export const applyPreferencesOnScopeChange = (
  previous: SearchParameters,
  next: SearchParameters,
  preferences: SearchPreferences,
  availability: SearchParameterAvailability
): SearchParameters =>
  scopeRememberedFor(next) === scopeRememberedFor(previous)
    ? next
    : applyPreferencesOnEntry(next, preferences, availability);
